import { Injectable, signal } from '@angular/core';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly kind: ToastKind;
}

const DEFAULT_TTL_MS = 4000;

/**
 * Avisos efêmeros (toasts) da plataforma. Empilha mensagens num signal que o
 * componente `Toast` renderiza; cada uma se auto-descarta após o TTL. Usado para
 * confirmar ações (criar/editar/excluir) já que parte do fluxo (convite por
 * e-mail) ainda não dá retorno visível.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private seq = 0;
  readonly toasts = signal<readonly Toast[]>([]);

  /**
   * Exibe um aviso.
   *
   * @param message texto curto exibido ao usuário
   * @param kind tom visual (sucesso / erro / informação)
   * @param ttlMs tempo até auto-descartar, em milissegundos
   */
  show(message: string, kind: ToastKind = 'success', ttlMs: number = DEFAULT_TTL_MS): void {
    const id = ++this.seq;
    this.toasts.update((list) => [...list, { id, message, kind }]);
    try {
      setTimeout(() => this.dismiss(id), ttlMs);
    } catch {
      /* ambiente sem timers: o aviso permanece até ser fechado manualmente */
    }
  }

  dismiss(id: number): void {
    this.toasts.update((list) => list.filter((toast) => toast.id !== id));
  }
}
